import { getBranchDetails, getTrailerDetailsEffect } from '@/redux/Savers/SaversEffects';
import { setAppLoader } from '@/redux/common/CommonActions';
import PrimaryLabel from '@/views/components/Labels/PrimaryLabel';
import AlertModal from '@/views/components/Modals/AlertModal';
import Drawer from '@/views/components/Modals/Drawer';
import TrailerTransferModal from '@/views/screens/Savers/TrailerTransferModal';
import { Form, Tabs } from 'antd';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import TrailerManagement from './TrailerManagement';
import { INITIAL_TRAILER_DETAILS, TAB_KEYS } from './constants';
import useFieldsData from './hooks/useFieldsData';
import {
  BuildDestInfo,
  BuildOrgInfo,
  BuildProdDetailsInfo,
  CreateMovementData,
  DestinationDataBuilder,
  OriginDataBuilder,
  createShipmentData,
  getShipmentDetails,
} from './utils';

const TrailerMainPage = () => {
  const [formPickupManagement] = Form.useForm();
  const [formDeliveryManagement] = Form.useForm();
  const dispatch = useDispatch();
  const { state } = useLocation();

  const userType = state?.userType;
  const shipmentData = state?.data?.[0] ?? {};
  const { isMultiple, pickupStatus, deliveryStatus, pickupLabel, deliveryLabel, nonMultiLabel, nonMultiStatus } =
    getShipmentDetails(shipmentData);
  const companyId = state.companyId;
  const requestID = state.requestID;
  const requestUserType = state.requestUserType;

  const suppliers = useSelector((state) => state.savers.supplierList.data);

  const [activeTabName, setActiveTabName] = useState(TAB_KEYS.PICKUP);
  const [pickupFormValues, setPickupFormValues] = useState(INITIAL_TRAILER_DETAILS);
  const [deliveryFormValues, setDeliveryFormValues] = useState(INITIAL_TRAILER_DETAILS);
  const [trailerId, setTrailerId] = useState(0);
  const [transferhidden, setTransferHidden] = useState(false);
  const [saveShipmentData, setSaveShipmentData] = useState([]);
  const [saveMovementData, setSaveMovementData] = useState([]);
  const [companyIdVal, setCompanyIdVal] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [userStatus, setUserStatus] = useState('');
  const [pickupVendorId, setPickupVendorId] = useState('');
  const [deliveryVendorId, setDeliveryVendorId] = useState('');

  const [checkOptions, setCheckOptions] = useState({
    rollDoor: false,
    directFromPartner: false,
  });
  const { createdBy, shipmentNumber, branchKey, poNumberValue, soNumberValue, unitSize } = useFieldsData(
    companyId,
    requestID,
    formPickupManagement,
    formDeliveryManagement,
    setPickupFormValues,
    setDeliveryFormValues,
    isMultiple,
    nonMultiLabel,
    setCheckOptions,
    setPickupVendorId,
    setDeliveryVendorId,
    activeTabName,
  );

  const handleValuesChange = (changedValues) => {
    const deepMerge = (prevValues, newValues) => {
      return Object.keys(newValues).reduce(
        (acc, key) => {
          if (Array.isArray(newValues[key]) && Array.isArray(prevValues[key])) {
            acc[key] = newValues[key].map((item, index) => ({
              ...prevValues[key][index],
              ...item,
            }));
          } else {
            acc[key] = newValues[key];
          }
          return acc;
        },
        { ...prevValues },
      );
    };
    if (changedValues.orgLocations) {
      changedValues.orgLocations = changedValues.orgLocations.map((location) => {
        if (location.orgShipLocation) {
          return {
            ...location,
            orgLocationId: location.orgShipLocation.value,
            orgShipLocation: location.orgShipLocation.label,
          };
        }
        return location;
      });
    }

    if (changedValues.Commodity) {
      changedValues.productType = changedValues.Commodity.value;
      changedValues.Commodity = changedValues.Commodity.label;
    }

    if (changedValues.destLocations) {
      changedValues.destLocations = changedValues.destLocations.map((location) => {
        if (location.destConsignee) {
          return {
            ...location,
            destLocationId: location.destConsignee.value,
            destConsignee: location.destConsignee.label,
          };
        }
        return location;
      });
    }
    if (activeTabName === TAB_KEYS.PICKUP) {
      setPickupFormValues((prevValues) => deepMerge(prevValues, changedValues));
    } else if (activeTabName === TAB_KEYS.DELIVERY) {
      setDeliveryFormValues((prevValues) => deepMerge(prevValues, changedValues));
    }
  };

  const handleTrailerIdChange = async () => {
    try {
      dispatch(setAppLoader(true));
      const { data } = await getTrailerDetailsEffect(trailerId);
      if (!data || data.length === 0) return;
      const trailer = data[0];
      if (!isMultiple) {
        setPickupVendorId(trailer.vendorId);
      } else {
        if (activeTabName === TAB_KEYS.PICKUP) {
          setPickupVendorId(trailer.vendorId);
        } else {
          setDeliveryVendorId(trailer.vendorId);
        }
      }
      const getOrgLocations = (shouldNullifyLocation, existingOrgLocations = []) =>
        data.map((trailer, index) => ({
          orgShipLocation: shouldNullifyLocation
            ? (existingOrgLocations[index]?.orgShipLocation ?? null)
            : trailer.branchDesc,
          orgSpotDate: moment(trailer.loadedDate ?? undefined),
          orgSoftWeight: trailer.soft,
          orgSoftOks: trailer.softOks,
          orgMiscWeight: trailer.misc,
          orgMiscOks: trailer.miscOKs,
          orgBookWeight: trailer.book,
          orgBookOks: trailer.bookOks,
          orgFurn: trailer.furn,
          orgOLI: trailer.oli,
          orgLocationId: trailer.locationId,
        }));

      const getDestLocations = (existingDestLocations) =>
        data.map((trailer, index) => ({
          destConsignee: existingDestLocations[index]?.destConsignee,
          destETA: null,
          destSoftWeight: trailer.soft,
          destSoftOks: trailer.softOks,
          destMiscWeight: trailer.misc,
          destMiscOks: trailer.miscOKs,
          destBookWeight: trailer.book,
          destBookOks: trailer.bookOks,
          destFurn: trailer.furn,
          destOLI: trailer.oli,
          destLocationId: existingDestLocations[index]?.destLocationId,
        }));

      const commonValues = {
        UnitSize: unitSize,
        PickupRentalCompany:
          (trailer.unitRentalCompany && trailer.unitRentalCompany) !== 'NULL' ? trailer.unitRentalCompany : null,
        DeliveryRentalCompany: trailer.unitRentalCompany,
        entryDate: trailer.entryDate && moment(trailer.entryDate),
      };
      setCheckOptions((prev) => ({
        ...prev,
        rollDoor: trailer.rollDoor,
      }));

      if (userStatus.toLowerCase().includes('pickup')) {
        const existingPickupValues = formPickupManagement.getFieldsValue();
        const finalPickupValues = {
          ...existingPickupValues,
          ...commonValues,
          orgLocations: getOrgLocations(true, existingPickupValues.orgLocations),
          destLocations: getDestLocations(existingPickupValues.destLocations),
        };

        formPickupManagement.setFieldsValue(finalPickupValues);
        setPickupFormValues(finalPickupValues);
      } else if (isMultiple && userStatus.toLowerCase().includes('delivery')) {
        const existingDeliveryValues = formDeliveryManagement.getFieldsValue();
        const finalDeliveryValues = {
          ...existingDeliveryValues,
          ...commonValues,
          orgLocations: getOrgLocations(false, existingDeliveryValues.orgLocations),
          destLocations: getDestLocations(existingDeliveryValues.destLocations),
        };
        formDeliveryManagement.setFieldsValue(finalDeliveryValues);
        setDeliveryFormValues(finalDeliveryValues);
      } else {
        const existingDeliveryValues = formPickupManagement.getFieldsValue();
        const finalDeliveryValues = {
          ...existingDeliveryValues,
          ...commonValues,
          orgLocations: getOrgLocations(false, existingDeliveryValues.orgLocations),
          destLocations: getDestLocations(existingDeliveryValues.destLocations),
        };
        formPickupManagement.setFieldsValue(finalDeliveryValues);
        setPickupFormValues(finalDeliveryValues);
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  const handleUserStatus = (data) => {
    setUserStatus(data);
  };

  useEffect(() => {
    const selectedPartner =
      activeTabName === TAB_KEYS.PICKUP ? pickupFormValues.selPartner : deliveryFormValues.selPartner;

    if (selectedPartner && suppliers?.length) {
      const foundRow = suppliers.find((row) => row.branchCustRef === selectedPartner);
      setCompanyIdVal(foundRow?.billToName || '');
    }
  }, [suppliers, activeTabName, pickupFormValues.selPartner, deliveryFormValues.selPartner]);

  useEffect(() => {
    handleTrailerIdChange();
  }, [trailerId]);

  const changeActiveTab = (key) => {
    setActiveTabName(key);
  };

  const validateField = (field, message) => {
    if (!field) {
      setAlertMessage(message);
      setAlertOpen(true);
      return false;
    }
    return true;
  };

  const validateDates = () => {
    const fieldsPickup = formPickupManagement.getFieldsValue();
    const pickupSpotDate = fieldsPickup.orgLocations?.[0]?.orgSpotDate;
    const pickupETA = fieldsPickup.destLocations?.[0]?.destETA;
    const pickupETD = fieldsPickup.orgLocations?.[0]?.orgETD;

    if (
      !validateField(pickupSpotDate, 'Please select a spot date') ||
      !validateField(pickupETA, 'Please select an ETA date') ||
      !validateField(pickupETD, 'Please select an ETD date')
    ) {
      return false;
    }

    if (!moment(pickupETD).isAfter(moment(pickupSpotDate))) {
      setAlertMessage('ETD must be after the spot date');
      setAlertOpen(true);
      return false;
    }

    if (!moment(pickupETA).isAfter(moment(pickupSpotDate))) {
      setAlertMessage('ETA must be after the spot date');
      setAlertOpen(true);
      return false;
    }

    if (isMultiple && userStatus.toLowerCase().includes('delivery')) {
      const fieldsDelivery = formDeliveryManagement.getFieldsValue();
      const deliverySpotDate = fieldsDelivery.orgLocations?.[0]?.orgSpotDate;
      const deliveryETA = fieldsDelivery.destLocations?.[0]?.destETA;
      const deliveryETD = fieldsDelivery.orgLocations?.[0]?.orgETD;

      if (
        !validateField(deliverySpotDate, 'Please select a spot date for delivery') ||
        !validateField(deliveryETA, 'Please select an ETA date for delivery') ||
        !validateField(deliveryETD, 'Please select an ETD date for delivery')
      ) {
        return false;
      }

      if (!moment(deliveryETD).isAfter(moment(deliverySpotDate))) {
        setAlertMessage('ETD for delivery must be after the spot date');
        setAlertOpen(true);
        return false;
      }

      if (!moment(deliveryETA).isAfter(moment(deliverySpotDate))) {
        setAlertMessage('ETA for delivery must be after the spot date');
        setAlertOpen(true);
        return false;
      }
    }

    return true;
  };

  const validateCommodity = () => {
    if (!isMultiple) {
      if (userStatus.toLowerCase().includes('loaded')) {
        const fields = formPickupManagement.getFieldsValue();

        const commodity = fields?.Commodity;
        if (!validateField(commodity, 'Please select a commodity')) {
          return false;
        }
      }
    }
    if (isMultiple) {
      const fieldsDelivery = formDeliveryManagement.getFieldsValue();
      const deliveryCommodity = fieldsDelivery?.Commodity;

      const fieldsPickup = formPickupManagement.getFieldsValue();
      const pickupCommodity = fieldsPickup?.Commodity;
      if (pickupLabel.toLocaleLowerCase().includes('loaded')) {
        if (!validateField(pickupCommodity, 'Please select a commodity for pickup')) {
          return false;
        }
      }

      if (deliveryLabel.toLowerCase().includes('loaded')) {
        if (!validateField(deliveryCommodity, 'Please select a commodity for delivery')) {
          return false;
        }
      }
    }

    return true;
  };

  const onFormSubmit = async () => {
    try {
      if (activeTabName === TAB_KEYS.PICKUP && isMultiple) {
        changeActiveTab(TAB_KEYS.DELIVERY);
        return;
      }

      if (!validateCommodity()) {
        return;
      }

      if (!validateDates()) {
        return;
      }

      dispatch(setAppLoader(true));

      const fetchDetails = async (locations, type) => {
        const detailsArray = await Promise.all(
          locations.map(async (trailerDetail) => {
            const locationId = Number.parseInt(
              type === 'org' ? trailerDetail.orgLocationId : trailerDetail.destLocationId,
            );
            const { data } = await getBranchDetails(locationId);

            if (data) {
              const prefix = type === 'org' ? 'org' : 'dest';
              Object.assign(data, {
                [`${prefix}name`]: data.branchDesc,
                [`${prefix}address1`]: data.branchAddr1,
                [`${prefix}city`]: data.branchCity,
                [`${prefix}state`]: data.branchState,
                [`${prefix}zip`]: data.branchZip,
                [`${prefix}country`]: data.countryCode,
                [`${prefix}custref`]: data.branchCustRef,
              });
            }
            return data;
          }),
        );

        return type === 'org'
          ? OriginDataBuilder(detailsArray, createdBy)
          : DestinationDataBuilder(detailsArray, createdBy);
      };

      const commonParams = {
        createdBy,
        shipmentNumber,
        companyId,
        branchKey,
        poNumberValue,
        soNumberValue,
        requestID,
        trailerId,
      };

      const processShipment = async (formValues) => ({
        shipmentOriginInformation: await fetchDetails(formValues.orgLocations, 'org'),
        shipmentDestinationInformation: await fetchDetails(formValues.destLocations, 'dest'),
        shipmentProductInformation: BuildProdDetailsInfo(formValues, createdBy),
        formValues,
      });

      const movementShipment = (formValues) => ({
        trailerShipping: BuildOrgInfo(formValues.orgLocations, trailerId),
        trailerRequest: BuildDestInfo(formValues.destLocations, trailerId),
      });

      const shipmentPayload = [createShipmentData({ ...commonParams, ...(await processShipment(pickupFormValues)) })];
      const movementPayload = [
        CreateMovementData({
          branchKey,
          requestID,
          formValues: pickupFormValues,
          checkOptions,
          ...movementShipment(pickupFormValues),
        }),
      ];
      if (isMultiple) {
        shipmentPayload.push(createShipmentData({ ...commonParams, ...(await processShipment(deliveryFormValues)) }));
        movementPayload.push(
          CreateMovementData({
            branchKey,
            requestID,
            formValues: deliveryFormValues,
            checkOptions,
            ...movementShipment(deliveryFormValues),
          }),
        );
      }
      setSaveMovementData(movementPayload);
      setSaveShipmentData(shipmentPayload);
      setTransferHidden(true);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  const CloseTransferModal = () => {
    setTransferHidden(!transferhidden);
  };

  const handleOptionToggle = (e) => {
    const { name } = e.target;
    setCheckOptions((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const commonProps = {
    handleValuesChange,
    isMultiple,
    userType,
    activeTabName,
    editMode: true,
    companyId,
    onFormSubmit,
    setTrailerId,
    checkOptions,
    handleOptionToggle,
    handleUserStatus,
  };

  const tabItems = useMemo(
    () => [
      {
        key: TAB_KEYS.PICKUP,
        label: TAB_KEYS.PICKUP,
        children: (
          <TrailerManagement
            {...commonProps}
            formTrailerRequestManagement={formPickupManagement}
            status={pickupStatus}
            label={pickupLabel}
            initialValues={pickupFormValues}
          />
        ),
      },
      {
        key: TAB_KEYS.DELIVERY,
        label: TAB_KEYS.DELIVERY,
        children: (
          <TrailerManagement
            {...commonProps}
            formTrailerRequestManagement={formDeliveryManagement}
            status={deliveryStatus}
            label={deliveryLabel}
            initialValues={deliveryFormValues}
          />
        ),
      },
    ],
    [pickupLabel, deliveryLabel, activeTabName, pickupFormValues, deliveryFormValues],
  );

  const onAlertConfirm = () => {
    setAlertOpen(!alertOpen);
  };

  return (
    <div>
      <>
        {isMultiple ? (
          <Tabs
            className="page-level-tab-container savers-tab"
            onChange={changeActiveTab}
            activeKey={activeTabName}
            items={tabItems}
          />
        ) : (
          <TrailerManagement
            {...commonProps}
            formTrailerRequestManagement={formPickupManagement}
            status={nonMultiStatus}
            label={nonMultiLabel}
            initialValues={pickupFormValues}
            requestUserType={requestUserType}
          />
        )}
      </>

      <Drawer
        width="50%"
        visible={transferhidden}
        headerContents={<PrimaryLabel className="text-white">Carrier Selection</PrimaryLabel>}
        onClose={CloseTransferModal}
      >
        <TrailerTransferModal
          trailerName={trailerId}
          movementdata={saveMovementData}
          shipmentdata={saveShipmentData}
          shipmentNo={shipmentNumber}
          trailerSizeValue={unitSize}
          companyIdVal={companyIdVal}
          handler={CloseTransferModal}
          isMultipleUserDetails={isMultiple}
          pickupVendorId={pickupVendorId}
          deliveryVendorId={deliveryVendorId}
          nonMultiLabel={nonMultiLabel}
          pickupLabel={pickupLabel}
          deliveryLabel={deliveryLabel}
        />
      </Drawer>
      <AlertModal
        title="Alert"
        open={alertOpen}
        description={alertMessage}
        onOk={onAlertConfirm}
        onClose={onAlertConfirm}
      />
    </div>
  );
};

export default TrailerMainPage;
